/**
 * useCustomerPricing Hook
 * 客戶定價管理 React Query Hook
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type {
    CustomerPricesResponse,
    EffectivePricesResponse,
    ProductCustomerPricesResponse,
    SetPriceRequest,
    BatchSetPriceRequest,
} from "@/lib/types";

/**
 * 取得客戶所有售價
 */
export function useCustomerPrices(customerId: number | null) {
    return useQuery<CustomerPricesResponse>({
        queryKey: [`/api/customers/${customerId}/prices`],
        enabled: !!customerId,
    });
}

const effectivePricesKey = (customerId: number | null) => `/api/customers/${customerId}/effective-prices`;

/**
 * 取得客戶對所有現行產品的有效售價＋來源（客戶專屬價優先，無則產品標準價）
 * J-05 殘項 C／C2：供下單表單預填單價；date＝表單的出貨日期（YYYY-MM-DD），後端以該日為生效日基準。
 * 兩元素 key [url, date]：每個日期各自快取；改價 mutation 用 [url] 前綴一次 invalidate 該客戶所有日期。
 * 沒有客戶或沒有日期時不發請求。
 */
export function useEffectivePrices(customerId: number | null, date: string) {
    return useQuery<EffectivePricesResponse>({
        queryKey: [effectivePricesKey(customerId), date],
        queryFn: async () => {
            const res = await apiRequest(
                "GET",
                `${effectivePricesKey(customerId)}?date=${encodeURIComponent(date)}`,
            );
            return res.json() as Promise<EffectivePricesResponse>;
        },
        enabled: !!customerId && !!date,
    });
}

/**
 * 設定單筆客戶售價
 */
export function useSetPrice() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ customerId, data }: { customerId: number; data: SetPriceRequest }) => {
            const res = await apiRequest("POST", `/api/customers/${customerId}/prices`, data);
            return await res.json();
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({
                queryKey: [`/api/customers/${variables.customerId}/prices`],
            });
            // R5：下單表單的預填價（所有日期）也要失效，否則剛調完價立刻建單會帶到舊價。
            queryClient.invalidateQueries({
                queryKey: [effectivePricesKey(variables.customerId)],
            });
        },
    });
}

/**
 * 批量設定客戶售價
 */
export function useBatchSetPrices() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ customerId, data }: { customerId: number; data: BatchSetPriceRequest }) => {
            const res = await apiRequest("POST", `/api/customers/${customerId}/prices/batch`, data);
            return await res.json();
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({
                queryKey: [`/api/customers/${variables.customerId}/prices`],
            });
            // R5：下單表單的預填價（所有日期）也要失效，否則剛調完價立刻建單會帶到舊價。
            queryClient.invalidateQueries({
                queryKey: [effectivePricesKey(variables.customerId)],
            });
        },
    });
}

/**
 * 反查產品在所有客戶的售價
 */
export function useProductCustomerPrices(productId: number | null) {
    return useQuery<ProductCustomerPricesResponse>({
        queryKey: [`/api/products/${productId}/customer-prices`],
        enabled: !!productId,
    });
}
